import { useState } from "react";
import moment from "moment/moment";
import { IconDotsVertical, IconGauge } from "@tabler/icons-react";
import Button from "../UI/Button";
import EmptyState from "../UI/EmptyState";
import IconButton from "../UI/IconButton";
import Text from "../UI/Text";
// @ts-expect-error pure js
import CompteurForm from "./CompteurForm";
import CompteurHistory from "./CompteurHistory";
import CompteurEditor, { type EditableCompteur } from "./CompteurEditor";
import useCompteurs, { type Compteur } from "../../hooks/use-compteur";
import { CompteurType } from "../../models/compteur";
import styles from "./CompteurShow.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import Title from "../UI/Title.tsx";
import ModalDialog from "../UI/ModalDialog";

interface CompteurShowProps {
  isFolder: boolean;
  propId: number | string;
}

const CARD_COLOR_CLASS: Record<string, string> = {
  eau: styles.cardEau,
  gaz: styles.cardGaz,
  elec: styles.cardElec,
};

export default function CompteurShow({ isFolder, propId }: CompteurShowProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<string | null>(null);
  const { groupedCompteurs, createCompteur, batchUpdateCompteurs } =
    useCompteurs(propId, isFolder);

  async function handleAddSubmit(values: Compteur) {
    await createCompteur(values);
    setAddOpen(false);
  }

  async function handleEditSave(
    compteurs: EditableCompteur[],
    deletedIds: number[],
  ) {
    await batchUpdateCompteurs(compteurs, deletedIds);
    setEditOpen(false);
  }

  function openHistory(type: string) {
    setSelectedType(type);
    setHistoryOpen(true);
  }

  function openEdit() {
    setEditingType(selectedType);
    setEditOpen(true);
  }

  const hasCompteurs = Object.keys(groupedCompteurs).length > 0;
  const selectedCompteurType = CompteurType.fromName(selectedType);
  const selectedCompteurs: Compteur[] = selectedType
    ? groupedCompteurs[selectedType]
    : [];
  const editingCompteurType = CompteurType.fromName(editingType);
  const editingCompteurs: Compteur[] = editingType
    ? groupedCompteurs[editingType]
    : [];

  return (
    <>
      <ModalDialog open={addOpen} onOpenChange={setAddOpen}>
        <CompteurForm onSubmit={handleAddSubmit} />
      </ModalDialog>

      <CompteurHistory
        open={historyOpen}
        onOpenChange={(open) => {
          setHistoryOpen(open);
          if (!open) setSelectedType(null);
        }}
        compteurType={selectedCompteurType}
        compteurs={selectedCompteurs}
        onEdit={openEdit}
      />

      <CompteurEditor
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) setEditingType(null);
        }}
        compteurType={editingCompteurType}
        compteurs={editingCompteurs}
        onSave={handleEditSave}
      />

      <Button type="button" onClick={() => setAddOpen(true)}>
        Ajouter un relevé
      </Button>

      {!hasCompteurs && (
        <EmptyState
          icon={IconGauge}
          title="Aucun compteur enregistré"
          description="Ajoutez un relevé pour suivre l'eau, le gaz et l'électricité."
        />
      )}

      <div className={styles.container}>
        {Object.entries(groupedCompteurs).map(([type, compteurs]) => {
          const compteurType = CompteurType.fromName(type);
          if (!compteurType) return null;
          const lastCompteur = compteurs[0];
          const numeroCompteur = compteurs.find((c) => c.numero_compteur)
            ?.numero_compteur;
          const colorClass = CARD_COLOR_CLASS[compteurType.type];

          return (
            <div key={type} className={classnames(styles.card, colorClass)}>
              <div className={styles.cardHeader}>
                <div>
                  <Title variant="hecto" weight="bold">
                    {compteurType.description}
                  </Title>
                  {numeroCompteur && (
                    <Text variant="deci">N° {numeroCompteur}</Text>
                  )}
                </div>
                <IconButton
                  icon={IconDotsVertical}
                  label="Voir l'historique"
                  onClick={() => openHistory(type)}
                />
              </div>

              <div className={styles.cardContent}>
                <div className={styles.row}>
                  <Text as="span" weight="medium">
                    Nombre de relevés
                  </Text>
                  <Text as="span" weight="medium">
                    {compteurs.length}
                  </Text>
                </div>
                <div className={styles.row}>
                  <Text as="span" weight="medium">
                    Dernier relevé
                  </Text>
                  <Text as="span" weight="medium">
                    {moment(lastCompteur.date_releve).format("DD/MM/YYYY")}
                  </Text>
                </div>
                <div className={styles.row}>
                  <Text as="span" weight="medium">
                    Index relevé
                  </Text>
                  <Text as="span" weight="bold">
                    {lastCompteur.valeur}
                    {compteurType.unite}
                  </Text>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
